import { useState } from 'react';
import { ClipboardList, FileText } from 'lucide-react';
import {
  Button,
  Textarea,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, Alert, useToast } from '@vnetwork/ui/feedback';
import { Section } from '@vnetwork/ui/layout';
import type { Ticket } from '@/api/tickets';
import { useUpdateTicket } from '@/api/tickets';
import {
  useStartTicket,
  useResolveTicket,
  useRollbackTicket,
  useCloseTicket,
  useEscalateTicket,
  useConvertIncidentToProblem,
  type StartTicketPayload,
} from '@/api/tickets-workflow';
import {
  TICKET_STATUS_OPENING,
  TICKET_STATUS_WORK_IN_PROGRESS,
  TICKET_STATUS_COMPLETED,
  TICKET_STATUS_CLOSED,
  TICKET_STATUS_ESCALATED,
  ERROR_SERVICE_INCIDENT_TICKET_KEY,
} from '@/api/tickets-constants';
import { TicketWorkflowStepper } from './ticket-workflow-stepper';
import { DateInput } from '@/components/date-input';

type DialogKind =
  | 'start'
  | 'rollback'
  | 'close'
  | 'escalate'
  | 'convert'
  | null;

interface WorkflowTabProps {
  ticket: Ticket;
}

/* ── Resolve form state ───────────────────────────────────────────────────── */

interface ResolveFormState {
  /* Normal resolve fields */
  ticket_remedial_measures: string;
  ticket_time_release: string; // YYYY-MM-DD
  /* RCA fields (incident only) */
  ticket_root_cause: string;
  ticket_avoid: string;        // CAPA / preventive measures
  ticket_internal_root_cause: string;
  ticket_affected: string;
}

function blankResolveForm(): ResolveFormState {
  return {
    ticket_remedial_measures: '',
    ticket_time_release: '',
    ticket_root_cause: '',
    ticket_avoid: '',
    ticket_internal_root_cause: '',
    ticket_affected: '',
  };
}

/* ── Closed/completed read-only report ───────────────────────────────────── */

function ClosedReport({ ticket }: { ticket: Ticket }) {
  const hasAny = !!(
    ticket.ticket_remedial_measures ||
    ticket.ticket_root_cause ||
    ticket.ticket_avoid ||
    ticket.ticket_internal_root_cause ||
    ticket.ticket_affected ||
    ticket.ticket_time_release
  );

  if (!hasAny) {
    return (
      <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        This ticket is closed. No resolution report was recorded.
      </p>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <FileText size={15} style={{ color: 'var(--vn-fg-muted)' }} />
        <span
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            fontWeight: 'var(--vn-font-weight-medium)',
          }}
        >
          Resolution Report
        </span>
      </div>

      {ticket.ticket_remedial_measures && (
        <Section title="Remedial Measures">
          <p
            style={{
              fontSize: 'var(--vn-font-size-sm)',
              color: 'var(--vn-fg-default)',
              whiteSpace: 'pre-wrap',
              margin: 0,
            }}
          >
            {ticket.ticket_remedial_measures}
          </p>
        </Section>
      )}

      {ticket.ticket_time_release && (
        <Section title="Time Release">
          <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)', margin: 0 }}>
            {ticket.ticket_time_release}
          </p>
        </Section>
      )}

      {ticket.ticket_root_cause && (
        <Section title="Root Cause">
          <p
            style={{
              fontSize: 'var(--vn-font-size-sm)',
              color: 'var(--vn-fg-default)',
              whiteSpace: 'pre-wrap',
              margin: 0,
            }}
          >
            {ticket.ticket_root_cause}
          </p>
        </Section>
      )}

      {ticket.ticket_avoid && (
        <Section title="CAPA / Prevention">
          <p
            style={{
              fontSize: 'var(--vn-font-size-sm)',
              color: 'var(--vn-fg-default)',
              whiteSpace: 'pre-wrap',
              margin: 0,
            }}
          >
            {ticket.ticket_avoid}
          </p>
        </Section>
      )}

      {ticket.ticket_affected && (
        <Section title="Affected">
          <p
            style={{
              fontSize: 'var(--vn-font-size-sm)',
              color: 'var(--vn-fg-default)',
              whiteSpace: 'pre-wrap',
              margin: 0,
            }}
          >
            {ticket.ticket_affected}
          </p>
        </Section>
      )}

      {ticket.ticket_internal_root_cause && (
        <Section title="Internal Root Cause">
          <p
            style={{
              fontSize: 'var(--vn-font-size-sm)',
              color: 'var(--vn-fg-default)',
              whiteSpace: 'pre-wrap',
              margin: 0,
            }}
          >
            {ticket.ticket_internal_root_cause}
          </p>
        </Section>
      )}
    </div>
  );
}

/* ── Resolve drawer ───────────────────────────────────────────────────────── */

interface ResolveDrawerProps {
  open: boolean;
  onClose: () => void;
  ticket: Ticket;
  isRCA: boolean;
}

function ResolveDrawer({ open, onClose, ticket, isRCA }: ResolveDrawerProps) {
  const toast = useToast();
  const [form, setForm] = useState<ResolveFormState>(blankResolveForm);
  const [errors, setErrors] = useState<Partial<Record<keyof ResolveFormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateMutation = useUpdateTicket();
  const resolveMutation = useResolveTicket();

  const isPending = updateMutation.isPending || resolveMutation.isPending;

  const set = (field: keyof ResolveFormState) => (
    e: { target: { value: string } },
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof ResolveFormState, string>> = {};
    if (isRCA) {
      if (!form.ticket_root_cause.trim()) next.ticket_root_cause = 'Root cause is required for incident tickets.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    setForm(blankResolveForm());
    setErrors({});
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitError(null);

    // Build only the resolution-relevant fields for the update payload.
    // Preserve required fields from the current ticket to avoid overwriting them.
    const resolveFields: Record<string, unknown> = {
      ticket_remedial_measures: form.ticket_remedial_measures || undefined,
      ticket_time_release: form.ticket_time_release || undefined,
    };
    if (isRCA) {
      Object.assign(resolveFields, {
        ticket_root_cause: form.ticket_root_cause || undefined,
        ticket_avoid: form.ticket_avoid || undefined,
        ticket_internal_root_cause: form.ticket_internal_root_cause || undefined,
        ticket_affected: form.ticket_affected || undefined,
      });
    }

    updateMutation.mutate(
      {
        note: {
          ticket_id: ticket.ticket_id,
          ticket_status: ticket.ticket_status,
          ticket_priority: ticket.ticket_priority,
          ticket_type_id: ticket.ticket_type_id,
          ticket_request_id: ticket.ticket_request_id,
          ticket_source: ticket.ticket_source,
          ticket_content: ticket.ticket_content,
          ticket_requested_at: ticket.ticket_requested_at,
          ticket_time_response: ticket.ticket_time_response ?? '',
          ticket_time_repply: ticket.ticket_time_repply ?? '',
          ticket_resolved_at: ticket.ticket_resolved_at ?? '',
          ticket_department: String(ticket.ticket_department ?? ''),
          customer_id: ticket.customer_id,
          service_id: ticket.service_id,
          service_startdate: ticket.service_startdate ?? '',
          service_duedate: ticket.service_duedate ?? '',
          ...resolveFields,
        },
      },
      {
        onSuccess: () => {
          resolveMutation.mutate(
            { ticket_id: ticket.ticket_id },
            {
              onSuccess: () => {
                toast.success('Ticket resolved');
                handleClose();
              },
              onError: (err) => {
                const msg = err instanceof Error ? err.message : 'Failed to resolve.';
                setSubmitError(msg);
              },
            },
          );
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Failed to update ticket.';
          setSubmitError(msg);
        },
      },
    );
  };

  const title = isRCA ? 'Resolve — Incident RCA' : 'Resolve ticket';
  const description = isRCA
    ? 'Fill in the root-cause analysis fields before resolving this incident.'
    : 'Record the remedial measures applied before resolving.';

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DrawerContent size="md">
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerDescription>{description}</DrawerDescription>
        </DrawerHeader>

        <DrawerBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {submitError && <Alert tone="danger" title={submitError} />}

            {/* ── Normal resolve fields ─────────────────────────── */}
            <Field label="Remedial Measures" error={errors.ticket_remedial_measures}>
              {(p) => (
                <Textarea
                  {...p}
                  rows={4}
                  value={form.ticket_remedial_measures}
                  placeholder="Describe the measures applied to resolve the issue…"
                  onChange={set('ticket_remedial_measures')}
                />
              )}
            </Field>

            <Field label="Time Release" description="Date when the fix was released (YYYY-MM-DD)" error={errors.ticket_time_release}>
              {(p) => (
                <DateInput
                  {...p}
                  value={form.ticket_time_release}
                  onChange={set('ticket_time_release')}
                />
              )}
            </Field>

            {/* ── RCA-only fields (incident tickets) ───────────── */}
            {isRCA && (
              <>
                <Field label="Root Cause" required error={errors.ticket_root_cause}>
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={form.ticket_root_cause}
                      placeholder="What was the root cause of the incident?"
                      onChange={set('ticket_root_cause')}
                    />
                  )}
                </Field>

                <Field label="CAPA / Prevention" error={errors.ticket_avoid}>
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={form.ticket_avoid}
                      placeholder="Corrective and preventive actions to avoid recurrence…"
                      onChange={set('ticket_avoid')}
                    />
                  )}
                </Field>

                <Field label="Affected" error={errors.ticket_affected}>
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={2}
                      value={form.ticket_affected}
                      placeholder="Services, customers, or systems affected…"
                      onChange={set('ticket_affected')}
                    />
                  )}
                </Field>

                <Field label="Internal Root Cause Note" error={errors.ticket_internal_root_cause}>
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={2}
                      value={form.ticket_internal_root_cause}
                      placeholder="Internal note on root cause (not shared with customer)…"
                      onChange={set('ticket_internal_root_cause')}
                    />
                  )}
                </Field>
              </>
            )}
          </div>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="ghost" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isPending}>
            {isPending ? 'Resolving…' : 'Resolve'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

/* ── Main WorkflowTab ─────────────────────────────────────────────────────── */

/**
 * Workflow tab: stepper + status-aware action bar.
 *
 * Resolve is a two-step mutation:
 *   1. PUT /tickets/:id  — writes resolution fields (remedial_measures, time_release,
 *      root_cause, CAPA, affected, internal_root_cause for RCA tickets)
 *   2. PUT /tickets/:id/resolved — transitions status to completed
 *
 * Closed state: shows the read-only ClosedReport panel instead of the bare
 * "This ticket is closed." string.
 */
export function WorkflowTab({ ticket }: WorkflowTabProps) {
  const toast = useToast();
  const [open, setOpen] = useState<DialogKind>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [content, setContent] = useState('');
  const [contentError, setContentError] = useState(false);

  const startMutation = useStartTicket();
  const rollbackMutation = useRollbackTicket();
  const closeMutation = useCloseTicket();
  const escalateMutation = useEscalateTicket();
  const convertMutation = useConvertIncidentToProblem();

  const status = ticket.ticket_status;
  const isServiceTicket = ticket.ticket_type_group === 'service';
  const isIncident = ticket.ticket_type_key === ERROR_SERVICE_INCIDENT_TICKET_KEY;
  const isMajorIncident = ticket.ticket_marjorable?.[0] === true;
  const canConvert = isIncident && !isMajorIncident;

  // Incident tickets (error_service_incident) get the RCA form; others get the normal form.
  const isRCA = isIncident;

  const closeDialog = () => {
    setOpen(null);
    setReason('');
    setReasonError(false);
    setContent('');
    setContentError(false);
  };

  const onDone = (label: string) => () => {
    toast.success(label);
    closeDialog();
  };
  const onError = (label: string) => (err: unknown) => {
    const msg = err instanceof Error ? err.message : 'Something went wrong.';
    toast.error(`Failed to ${label}`, { description: msg });
  };

  const runStart = () => {
    const payload: StartTicketPayload = { ticket_id: ticket.ticket_id };
    startMutation.mutate(payload, {
      onSuccess: onDone('Ticket started'),
      onError: onError('start ticket'),
    });
  };

  const runRollback = () => {
    const trimmed = reason.trim();
    if (!trimmed) { setReasonError(true); return; }
    rollbackMutation.mutate(
      { ticket_id: ticket.ticket_id, content: trimmed },
      { onSuccess: onDone('Ticket rolled back'), onError: onError('rollback ticket') },
    );
  };

  const runClose = () => {
    closeMutation.mutate(
      { ticket_id: ticket.ticket_id },
      { onSuccess: onDone('Ticket closed'), onError: onError('close ticket') },
    );
  };

  const runEscalate = () => {
    const trimmed = reason.trim();
    if (!trimmed) { setReasonError(true); return; }
    escalateMutation.mutate(
      { ticket_id: ticket.ticket_id, reason: trimmed },
      { onSuccess: onDone('Ticket escalated'), onError: onError('escalate ticket') },
    );
  };

  const runConvert = () => {
    const trimmed = content.trim();
    if (!trimmed) { setContentError(true); return; }
    convertMutation.mutate(
      { ticket_id: ticket.ticket_id, content: trimmed },
      { onSuccess: onDone('Converted to problem ticket'), onError: onError('convert ticket') },
    );
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <ClipboardList size={16} style={{ color: 'var(--vn-fg-muted)' }} />
        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
          Processing Workflow
        </span>
      </div>

      {/* Stepper */}
      <TicketWorkflowStepper
        currentStatus={status}
        isServiceTicket={isServiceTicket}
      />

      {/* Action bar — rendered only for mutable states */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--vn-space-2)',
          paddingTop: 'var(--vn-space-2)',
          borderTop: '1px solid var(--vn-border-default)',
        }}
      >
        {status === TICKET_STATUS_OPENING && (
          <Button size="sm" variant="primary" onClick={() => setOpen('start')}>
            Start
          </Button>
        )}

        {(status === TICKET_STATUS_WORK_IN_PROGRESS ||
          status === TICKET_STATUS_ESCALATED) && (
          <>
            <Button size="sm" variant="primary" onClick={() => setResolveOpen(true)}>
              Resolve
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setOpen('escalate')}>
              Escalate
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen('rollback')}>
              Rollback
            </Button>
            {canConvert && (
              <Button size="sm" variant="ghost" onClick={() => setOpen('convert')}>
                Convert to Problem
              </Button>
            )}
          </>
        )}

        {status === TICKET_STATUS_COMPLETED && !isServiceTicket && (
          <Button size="sm" variant="primary" onClick={() => setOpen('close')}>
            Close
          </Button>
        )}

        {/* Closed state: show the read-only resolution report */}
        {status === TICKET_STATUS_CLOSED && <ClosedReport ticket={ticket} />}
      </div>

      {/* Resolve Drawer — structured form with update-then-resolve sequence */}
      <ResolveDrawer
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        ticket={ticket}
        isRCA={isRCA}
      />

      {/* Start dialog */}
      <ConfirmDialog
        open={open === 'start'}
        onOpenChange={(o) => (o ? setOpen('start') : closeDialog())}
        title="Start ticket"
        description="Move this ticket to Work In Progress. The technical team will begin processing it."
        confirmLabel="Start"
        loading={startMutation.isPending}
        onConfirm={runStart}
      />

      {/* Rollback dialog — reason required */}
      <ConfirmDialog
        open={open === 'rollback'}
        onOpenChange={(o) => (o ? setOpen('rollback') : closeDialog())}
        title="Rollback ticket"
        description="Return this ticket to Opening. Provide a reason."
        confirmLabel="Rollback"
        destructive
        loading={rollbackMutation.isPending}
        onConfirm={runRollback}
      >
        <Field
          label="Reason"
          required
          error={reasonError ? 'A reason is required.' : undefined}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              placeholder="Why is this ticket being rolled back?"
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError(false);
              }}
            />
          )}
        </Field>
      </ConfirmDialog>

      {/* Close dialog */}
      <ConfirmDialog
        open={open === 'close'}
        onOpenChange={(o) => (o ? setOpen('close') : closeDialog())}
        title="Close ticket"
        description="Permanently close this ticket. This action cannot be undone."
        confirmLabel="Close"
        destructive
        loading={closeMutation.isPending}
        onConfirm={runClose}
      />

      {/* Escalate dialog — reason required */}
      <ConfirmDialog
        open={open === 'escalate'}
        onOpenChange={(o) => (o ? setOpen('escalate') : closeDialog())}
        title="Escalate ticket"
        description="Escalate this ticket to the Team Leader / IT-ISMS Head. Provide a reason."
        confirmLabel="Escalate"
        loading={escalateMutation.isPending}
        onConfirm={runEscalate}
      >
        <Field
          label="Reason"
          required
          error={reasonError ? 'A reason is required.' : undefined}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              placeholder="Why is this ticket being escalated?"
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError(false);
              }}
            />
          )}
        </Field>
      </ConfirmDialog>

      {/* Convert incident → problem dialog */}
      <ConfirmDialog
        open={open === 'convert'}
        onOpenChange={(o) => (o ? setOpen('convert') : closeDialog())}
        title="Convert incident to problem"
        description="This creates a new problem ticket from the incident. Provide a description."
        confirmLabel="Convert"
        loading={convertMutation.isPending}
        onConfirm={runConvert}
      >
        <Field
          label="Content"
          required
          error={contentError ? 'Content is required.' : undefined}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={4}
              value={content}
              placeholder="Describe the problem derived from this incident…"
              onChange={(e) => {
                setContent(e.target.value);
                if (contentError) setContentError(false);
              }}
            />
          )}
        </Field>
      </ConfirmDialog>
    </div>
  );
}
